<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import DocumentDeliveryOptions from '@/components/revenue/DocumentDeliveryOptions.vue';
import { CONTRACT_DELIVERY_OPTIONS, contractDeliveryNeedsEmail, contractDeliveryActionLabel, type ContractDeliveryMethod } from '~/lib/contractDeliveryOptions';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Send, Upload, X } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import ContractVariablePanel from '@/components/contracts/ContractVariablePanel.vue';
import ContractTableEditor from '@/components/contracts/ContractTableEditor.vue';
import ProposalPdfReader from '@/components/proposals/ProposalPdfReader.vue';
import TemplatePicker from '@/components/templates/TemplatePicker.vue';
import { contractTemplateDraft } from '~/lib/contractTemplateValues';
import { useContractTemplateSync } from '~/composables/contracts/useContractTemplateSync';
import { missingContractFields } from '~/lib/contractDocument';
import type { ProjectContract, Proposal, UploadedFile, ContractFieldDefinition, ContractTableDefinition, ContractTableValues, ContractTemplate } from '~/types/models';
import { useContractTemplatesStore } from '~/stores/contractTemplates';

interface Props {
    canMutate: boolean;
    clientId: string;
    recipientName?: string;
    recipientEmail?: string;
    clientProposals: Proposal[];
    clientProposalsLoading: boolean;
    clientContract: ProjectContract | null;
    clientContractFiles: UploadedFile[];
    linkedClientContractFile: UploadedFile | null;
    hasSelectedClientContractContent: boolean;
    clientContractPdfDownloading: boolean;
    clientContractSaving: boolean;
    clientContractSending: boolean;
    clientContractUploading: boolean;
    onClientContractFileSelected: (event: Event) => void;
    downloadClientContractPdf: () => void | Promise<void>;
    resetClientContractDraft: () => void;
    saveClientContractDraft: () => void | Promise<void>;
    sendClientContract: () => void | Promise<void>;
    formatShortDate: (value: unknown, fallback?: string) => string;
    formatContractSigner: (signature?: { signerName?: string; signerEmail?: string } | null) => string;
}

const props = defineProps<Props>();

const isClientContractDialogOpen = defineModel<boolean>('clientOpen', { default: false });
const clientContractTitle = defineModel<string>('title', { default: '' });
const clientContractIntroMessage = defineModel<string>('introMessage', { default: '' });
const clientContractBodyText = defineModel<string>('bodyText', { default: '' });
const clientContractContentMode = defineModel<'text' | 'file'>('contentMode', { default: 'text' });
const linkedClientContractFileId = defineModel<string>('fileId', { default: '' });
const documentHtml = defineModel<string>('documentHtml', { default: '' });
const fields = defineModel<ContractFieldDefinition[]>('fields', { default: [] });
const tables = defineModel<ContractTableDefinition[]>('tables', { default: [] });
const values = defineModel<Record<string, string>>('values', { default: {} });
const proposalReferences = defineModel<Record<string, string>>('proposalReferences', { default: () => ({}) });
const proposalReferenceLinks = defineModel<Record<string, string>>('proposalReferenceLinks', { default: () => ({}) });
const tableValues = defineModel<ContractTableValues>('tableValues', { default: () => ({}) });
const templateId = defineModel<string>('templateId', { default: '' });
const deliveryMethod = defineModel<ContractDeliveryMethod>('deliveryMethod', { default: 'email' });
const needsEmail = computed(() => contractDeliveryNeedsEmail(deliveryMethod.value));
const sendLabel = computed(() => contractDeliveryActionLabel(deliveryMethod.value));
const tenantSignatureName = defineModel<string>('tenantSignatureName', { default: '' });
const templatesStore = useContractTemplatesStore();
const previewOpen = ref(false);
const previewLoading = ref(false);
const previewBlob = ref<Blob | null>(null);
const tenantStore = useTenantStore();
const { tenantPath } = useTenantPath();
const missingFields = computed(() => clientContractContentMode.value === 'text' && documentHtml.value
    ? missingContractFields(documentHtml.value, fields.value, values.value)
    : []);
const missingSignatures = computed(() => clientContractContentMode.value === 'text' && documentHtml.value
    ? (['tenant', 'client'] as const).filter(role => !documentHtml.value.includes(`data-contract-signature="${role}"`))
    : []);
function applyTemplateEntries(template: ContractTemplate, preserve: boolean) {
    const draft = contractTemplateDraft(template, preserve ? {
        values: values.value, proposalReferences: proposalReferences.value,
        proposalReferenceLinks: proposalReferenceLinks.value, tableValues: tableValues.value,
    } : undefined);
    documentHtml.value = draft.documentHtml;
    fields.value = draft.fields;
    tables.value = draft.tables;
    values.value = draft.values;
    proposalReferences.value = draft.proposalReferences;
    proposalReferenceLinks.value = draft.proposalReferenceLinks;
    tableValues.value = draft.tableValues;
}
function applyTemplate(id: string) {
    if (!id) {
        templateId.value = '';
        documentHtml.value = '';
        fields.value = [];
        tables.value = [];
        values.value = {};
        proposalReferences.value = {};
        proposalReferenceLinks.value = {};
        tableValues.value = {};
        return;
    }
    const template = templatesStore.templates.find(item => item.id === id);
    if (!template) return;
    const preserve = templateId.value === id;
    applyTemplateEntries(template, preserve);
    templateId.value = template.id;
    clientContractContentMode.value = 'text';
    if (!preserve) clientContractTitle.value = template.title;
    clientContractBodyText.value = '';
}
const canPreview = computed(() => clientContractContentMode.value === 'text' ? Boolean(documentHtml.value || clientContractBodyText.value) : Boolean(props.linkedClientContractFile?.fileURL));
const backdropImageUrl = computed(() => tenantStore.currentTenant?.paymentSettings?.emailBackgroundImageURL || undefined);
async function openPreview() {
    const tenantId = tenantStore.currentTenantId;
    if (!tenantId) return void toast.error('Select a workspace to preview this contract.');
    if (!props.clientId) return void toast.error('Open a client record to preview this contract.');
    const token = await (useNuxtApp().$auth.currentUser || useAuthStore().user)?.getIdToken();
    if (!token) return void toast.error('Sign in to preview this contract.');
    previewLoading.value = true;
    try {
        previewBlob.value = await $fetch<Blob>('/api/contracts/reader-preview', {
            method: 'POST',
            headers: { authorization: `Bearer ${token}` },
            body: {
                tenantId,
                clientId: props.clientId,
                ...(clientContractContentMode.value === 'file'
                    ? { fileId: linkedClientContractFileId.value }
                    : {
                        title: clientContractTitle.value,
                        introMessage: clientContractIntroMessage.value,
                        documentHtml: documentHtml.value,
                        bodyText: clientContractBodyText.value,
                        fieldValues: values.value,
                        proposalReferences: proposalReferences.value,
                        proposalReferenceLinks: proposalReferenceLinks.value,
                        tableDefinitions: tables.value,
                        tableValues: tableValues.value,
                    }),
            },
            responseType: 'blob',
        });
        isClientContractDialogOpen.value = false;
        previewOpen.value = true;
    } catch (error: any) {
        let serverMessage = error?.data?.statusMessage;
        if (!serverMessage && error?.data instanceof Blob) {
            try { serverMessage = JSON.parse(await error.data.text())?.statusMessage; }
            catch { /* Keep the fallback message for non-JSON errors. */ }
        }
        toast.error(serverMessage || error?.message || 'Could not prepare the contract preview.');
    } finally { previewLoading.value = false; }
}
function closePreview() {
    previewOpen.value = false;
    previewBlob.value = null;
    isClientContractDialogOpen.value = true;
}
function onPreviewKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && previewOpen.value) closePreview();
}
onMounted(() => window.addEventListener('keydown', onPreviewKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onPreviewKeydown));
function openPdf() {
    if (clientContractContentMode.value === 'file' && props.linkedClientContractFile?.fileURL) {
        window.open(props.linkedClientContractFile.fileURL, '_blank', 'noopener,noreferrer');
    } else if (previewBlob.value) {
        const url = URL.createObjectURL(previewBlob.value);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
}

const clientContractFileOptions = computed(() => props.clientContractFiles.map(file => ({
    value: file.id,
    label: file.fileName,
})));

const canEditContract = computed(() => props.canMutate && (!props.clientContract
    || props.clientContract.status === 'draft'
    || props.clientContract.status === 'declined'));
const canResendContract = computed(() => props.canMutate && props.clientContract?.status === 'awaiting_client');
const { loading: templatesLoading, refreshTemplate } = useContractTemplateSync({
    open: () => isClientContractDialogOpen.value,
    enabled: () => canEditContract.value && clientContractContentMode.value === 'text',
    templateId: () => templateId.value,
    onRefresh: template => applyTemplateEntries(template, true),
    onError: error => toast.error(error instanceof Error ? error.message : 'Unable to load contract templates.'),
});

const contractStatusLabel = computed(() => {
    if (props.clientContract?.status === 'signed') return 'Signed';
    if (props.clientContract?.status === 'awaiting_client') return 'Awaiting client signature';
    if (props.clientContract?.status === 'declined') return 'Declined — revision required';
    return 'Draft';
});
</script>

<template>
    <Sheet v-model:open="isClientContractDialogOpen">
        <SheetContent class="flex h-full !w-full !max-w-none flex-col gap-0 overflow-hidden p-0 sm:!w-[min(90vw,36rem)]">
            <SheetHeader class="shrink-0 border-b border-border/70 px-6 py-5 text-left">
                <SheetTitle>{{ clientContract ? 'Manage client contract' : 'Create client contract' }}</SheetTitle>
                <SheetDescription>
                    Keep the agreement on the client record while it still syncs to the portal.
                </SheetDescription>
            </SheetHeader>

            <div class="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <section class="rounded-xl border bg-muted/30 p-5 space-y-3">
                    <div class="flex flex-wrap items-center gap-2">
                        <Badge
                            v-if="clientContract"
                            :variant="clientContract.status === 'signed' ? 'default' : clientContract.status === 'awaiting_client' ? 'secondary' : clientContract.status === 'declined' ? 'destructive' : 'outline'"
                        >
                            {{ contractStatusLabel }}
                        </Badge>
                        <Badge v-if="clientContract" variant="outline">Version {{ clientContract.version }}</Badge>
                    </div>
                    <p class="text-sm text-muted-foreground">
                        {{ !clientContract
                            ? 'Once sent, the contract appears in the client portal for signature.'
                            : clientContract.status === 'draft'
                                ? 'This draft is saved internally. It will appear in the client portal after you sign and send it.'
                                : clientContract.status === 'declined'
                                    ? 'The client declined this version. Update the agreement and send a new revision when it is ready.'
                                    : 'The approval flow has started. The agreement is locked to preserve this contract version.' }}
                    </p>
                    <div class="space-y-2 text-sm">
                        <p><span class="font-medium text-foreground">Tenant signature:</span> {{ formatContractSigner(clientContract?.tenantSignature) }}</p>
                        <p v-if="clientContract?.tenantSignature?.signedAt" class="text-muted-foreground">
                            Signed {{ props.formatShortDate(clientContract.tenantSignature.signedAt, 'Not signed yet') }}
                        </p>
                        <p><span class="font-medium text-foreground">Client signature:</span> {{ formatContractSigner(clientContract?.clientSignature) }}</p>
                        <p v-if="clientContract?.clientSignature?.signedAt" class="text-muted-foreground">
                            Signed {{ props.formatShortDate(clientContract.clientSignature.signedAt, 'Not signed yet') }}
                        </p>
                    </div>
                </section>

                <section v-if="canEditContract" class="space-y-5 border-t border-border/70 pt-6">
                    <div>
                        <h3 class="text-sm font-semibold text-foreground">Agreement details</h3>
                        <p class="mt-1 text-sm text-muted-foreground">Choose a contract template or attach a PDF for this client.</p>
                    </div>
                    <div class="space-y-4">
                        <div class="space-y-2">
                            <Label for="client-contract-title">Contract title</Label>
                            <Input
                                id="client-contract-title"
                                v-model="clientContractTitle"
                                placeholder="Master services agreement"
                            />
                        </div>
                        <div class="space-y-2">
                            <Label>Content source</Label>
                            <div class="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    :variant="clientContractContentMode === 'text' ? 'default' : 'outline'"
                                    class="rounded-full"
                                    @click="clientContractContentMode = 'text'"
                                >
                                    Use template
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    :variant="clientContractContentMode === 'file' ? 'default' : 'outline'"
                                    class="rounded-full"
                                    @click="clientContractContentMode = 'file'"
                                >
                                    Upload PDF
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div class="space-y-2">
                    <Label for="client-contract-intro">Intro message</Label>
                    <Textarea
                        id="client-contract-intro"
                        v-model="clientContractIntroMessage"
                        rows="3"
                        class="min-h-[88px]"
                        placeholder="A short note before the agreement details."
                    />
                    </div>
                </section>

                <section v-if="canEditContract && clientContractContentMode === 'text'" class="space-y-3 border-t border-border/70 pt-6">
                    <div>
                        <h3 class="text-sm font-semibold text-foreground">Contract template</h3>
                        <p class="mt-1 text-sm text-muted-foreground">Select a prepared agreement, then fill its variables and table rows for this client.</p>
                    </div>
                    <div class="space-y-2">
                        <TemplatePicker id="client-contract-template" label="Contract template" :model-value="templateId" :options="templatesStore.templates.map(template => ({ value: template.id, label: template.title }))" :loading="templatesLoading" placeholder="Blank agreement" clear-label="Blank agreement" refresh-hint="Your entered values are kept." :edit-href="tenantPath({ path: '/contracts/templates', query: { template: templateId } })" @update:model-value="applyTemplate" @refresh="refreshTemplate" />
                        <p class="text-xs text-muted-foreground">Need a new agreement? <NuxtLink :to="tenantPath('/contracts/templates?new=1')" target="_blank" rel="noopener noreferrer" class="text-primary underline">Open the contract builder</NuxtLink>.</p>
                    </div>
                    <template v-if="templateId && documentHtml">
                        <div v-if="fields.length" class="space-y-3 border-t border-border/70 pt-5">
                            <ContractVariablePanel v-model:values="values" v-model:proposal-references="proposalReferences" v-model:proposal-reference-links="proposalReferenceLinks" :fields="fields" :proposals="clientProposals" :proposals-loading="clientProposalsLoading" :content-html="documentHtml" title="Fill contract variables" fill-only />
                            <p v-if="missingFields.length" class="text-sm text-destructive" role="status">Complete before sending: {{ missingFields.join(', ') }}.</p>
                        </div>
                        <ContractTableEditor v-if="tables.length" v-model="tableValues" :tables="tables" :disabled="!canEditContract" />
                        <p v-if="missingSignatures.length" class="text-sm text-destructive" role="status">This template needs {{ missingSignatures.join(' and ') }} signature positions. <NuxtLink :to="tenantPath({ path: '/contracts/templates', query: { template: templateId } })" target="_blank" rel="noopener noreferrer" class="underline">Edit it in the contract builder</NuxtLink> before sending.</p>
                    </template>
                    <p v-else-if="clientContractBodyText" class="text-sm text-muted-foreground">This older contract was written directly. You can preview it, or select a template to create a new revision.</p>
                </section>

                <section v-else-if="canEditContract" class="space-y-4 border-t border-border/70 pt-6">
                    <div>
                        <h3 class="text-sm font-semibold text-foreground">Contract document</h3>
                        <p class="mt-1 text-sm text-muted-foreground">Upload a PDF or select one already stored on the client workspace.</p>
                    </div>
                    <div class="space-y-4">
                        <div class="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <p class="text-sm font-medium text-foreground">Linked contract document</p>
                                <p class="text-sm text-muted-foreground">Upload the signed source PDF or choose one that is already on the client workspace.</p>
                            </div>
                            <Label class="inline-flex cursor-pointer items-center gap-2 rounded-full border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/40">
                                <Upload class="size-4" />
                                {{ clientContractUploading ? 'Uploading...' : 'Upload PDF' }}
                                <input class="hidden" type="file" accept="application/pdf" :disabled="clientContractUploading" @change="onClientContractFileSelected" />
                            </Label>
                        </div>

                        <div class="space-y-2">
                            <Label for="linked-client-contract-file">Uploaded contract</Label>
                            <SearchableSelectMenu
                                id="linked-client-contract-file"
                                v-model="linkedClientContractFileId"
                                :options="clientContractFileOptions"
                                placeholder="No linked document"
                                search-placeholder="Search client files..."
                                allow-clear
                                clear-label="No linked document"
                                show-all-by-default
                                variant="form"
                            />
                        </div>

                        <div v-if="linkedClientContractFile" class="rounded-lg border bg-muted/30 p-3 text-sm">
                            <p class="font-medium text-foreground">{{ linkedClientContractFile.fileName }}</p>
                            <a
                                :href="linkedClientContractFile.fileURL"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="text-primary underline"
                            >
                                Open linked document
                            </a>
                        </div>
                    </div>
                </section>

                <section v-else class="space-y-5 border-t border-border/70 pt-6">
                    <div>
                        <h3 class="text-sm font-semibold text-foreground">Agreement being reviewed</h3>
                        <p class="mt-1 text-sm text-muted-foreground">This version is read-only while it is with the client.</p>
                    </div>
                    <dl class="divide-y divide-border/60 border-y border-border/60 text-sm">
                        <div class="grid gap-1 py-4 sm:grid-cols-[10rem_1fr] sm:gap-4"><dt class="text-muted-foreground">Contract title</dt><dd class="font-medium text-foreground">{{ clientContractTitle || 'Untitled contract' }}</dd></div>
                        <div v-if="clientContractIntroMessage" class="grid gap-1 py-4 sm:grid-cols-[10rem_1fr] sm:gap-4"><dt class="text-muted-foreground">Message</dt><dd class="whitespace-pre-wrap text-foreground">{{ clientContractIntroMessage }}</dd></div>
                        <div class="grid gap-1 py-4 sm:grid-cols-[10rem_1fr] sm:gap-4"><dt class="text-muted-foreground">Content</dt><dd class="text-foreground">{{ clientContractContentMode === 'file' ? 'Linked PDF document' : 'Written contract terms' }}</dd></div>
                    </dl>
                    <Button v-if="canPreview" type="button" variant="outline" :disabled="previewLoading" @click="openPreview">{{ previewLoading ? 'Preparing preview...' : 'Preview contract' }}</Button>
                </section>
                <DocumentDeliveryOptions
                    v-if="canEditContract || canResendContract"
                    v-model="deliveryMethod"
                    class="border-t border-border/70 pt-6"
                    :options="CONTRACT_DELIVERY_OPTIONS"
                    :recipient-name="recipientName"
                    :recipient-email="recipientEmail"
                    :show-recipient="needsEmail"
                    :disabled="clientContractSending || clientContractSaving"
                    empty-recipient-label="Add an email to the client contact before sending."
                    description="Email sends a public link for review and signing without a portal account. Portal sharing notifies existing portal users and does not invite the client."
                />
                <section v-if="canEditContract" class="space-y-3 border-t border-border/70 pt-6"><div><h3 class="text-sm font-semibold">Tenant signature</h3><p class="mt-1 text-sm text-muted-foreground">Type your full name to sign this contract version before sending.</p></div><Label for="client-contract-tenant-signature">Your full name</Label><Input id="client-contract-tenant-signature" v-model="tenantSignatureName" placeholder="Full name" /><p v-if="tenantSignatureName" class="contract-tenant-signature text-3xl">{{ tenantSignatureName }}</p><p class="text-xs text-muted-foreground">Your typed name is recorded with this contract version and appears in its signed copy.</p></section>
                <Button v-if="canEditContract && canPreview" type="button" variant="outline" class="w-full" :disabled="previewLoading" @click="openPreview">{{ previewLoading ? 'Preparing preview...' : 'Preview contract' }}</Button>
            </div>

            <SheetFooter class="sheet-safe-footer shrink-0 border-t border-border/70 bg-background/95 px-6 py-4 backdrop-blur">
                <div v-if="canEditContract" class="grid w-full grid-cols-2 gap-2">
                    <Button class="w-full" variant="outline" :disabled="clientContractSaving || clientContractSending || clientContractUploading" @click="resetClientContractDraft">Clear draft</Button>
                    <Button class="w-full" variant="outline" :disabled="clientContractSaving || clientContractSending || clientContractUploading" @click="saveClientContractDraft">{{ clientContractSaving ? 'Saving...' : 'Save draft' }}</Button>
                    <Button class="col-span-2 w-full" :disabled="!hasSelectedClientContractContent || missingFields.length > 0 || missingSignatures.length > 0 || !tenantSignatureName.trim() || (needsEmail && !recipientEmail) || clientContractSaving || clientContractSending || clientContractUploading" @click="sendClientContract">
                        <Send class="mr-1 size-4" />
                        {{ clientContractSending ? 'Sending…' : sendLabel }}
                    </Button>
                </div>
                <div v-else class="w-full space-y-2">
                <Button v-if="canResendContract" class="w-full" :disabled="clientContractSending || (needsEmail && !recipientEmail)" @click="sendClientContract">{{ clientContractSending ? 'Sending…' : 'Resend contract' }}</Button>
                <Button class="w-full" variant="outline" :disabled="clientContractPdfDownloading" @click="downloadClientContractPdf">
                    <FileText class="mr-1 size-4" />
                    {{ clientContractPdfDownloading ? 'Preparing PDF...' : 'Download signed PDF' }}
                </Button>
                </div>
            </SheetFooter>
        </SheetContent>
    </Sheet>

    <Teleport to="body">
        <div v-if="previewOpen && previewBlob" class="fixed inset-0 z-[130] overflow-y-auto bg-background" role="region" aria-label="Contract preview">
            <ProposalPdfReader :source="previewBlob" :title="clientContractTitle || linkedClientContractFile?.fileName || 'Contract'" :backdrop-image-url="backdropImageUrl" loading-label="Preparing contract…" error-label="The contract PDF could not be displayed." @open="openPdf">
                <template #actions><div class="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center"><div class="w-full max-w-xl rounded-t-2xl border-x border-t border-border bg-background/95 px-5 py-3 text-center text-sm font-medium text-foreground shadow-2xl backdrop-blur">Respond to contract</div></div></template>
            </ProposalPdfReader>
            <div class="pointer-events-none fixed inset-x-0 top-0 z-30 flex items-start justify-between gap-3 p-4">
                <span class="pointer-events-auto rounded-full bg-background/95 px-3 py-2 text-xs font-medium text-foreground shadow-sm">Preview only · Not sent to the client</span>
                <Button type="button" class="pointer-events-auto" variant="secondary" size="sm" @click="closePreview"><X class="mr-1 size-4" />Close preview</Button>
            </div>
        </div>
    </Teleport>

</template>

<style scoped>
@font-face { font-family: ContractDancingScript; src: url('/fonts/DancingScript.ttf') format('truetype'); font-display: swap; }
.contract-tenant-signature { font-family: ContractDancingScript, cursive; }
</style>
